import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import EmailIcon from '@mui/icons-material/Email';

const useStyles = makeStyles(() => ({
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: '15px',
      padding: '1rem'
    }
  },
  title: {
    textAlign: 'center',
    color: '#3a7735',
    fontWeight: 'bold'
  },
  content: {
    textAlign: 'center',
    padding: '2rem'
  },
  textField: {
    marginTop: '1rem',
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px'
    }
  },
  button: {
    backgroundColor: '#3a7735 !important',
    color: 'white !important',
    borderRadius: '25px !important',
    padding: '10px 30px !important',
    fontWeight: 'bold !important',
    '&:hover': {
      backgroundColor: '#2d5f2a !important'
    }
  },
  cancelButton: {
    color: '#3a7735 !important',
    borderRadius: '25px !important',
    padding: '10px 30px !important'
  }
}));

const EmailVerificationDialog = ({ open, onVerify, onCancel }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleVerify = async () => {
    if (!email) {
      setError(t('emailRequired') || 'נא להזין כתובת דוא"ל');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError(t('invalidEmail') || 'כתובת דוא"ל לא תקינה');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await onVerify(email);
    } catch (err) {
      setError(err.message || t('verificationFailed') || 'האימות נכשל');
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      className={classes.dialog}
    >
      <DialogTitle className={classes.title}>
        <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
          <EmailIcon sx={{ fontSize: '2rem', color: '#3a7735' }} />
          <Typography variant="h5">
            {t('verifyEmail') || 'אימות כתובת דוא"ל'}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent className={classes.content}>
        <Typography sx={{ marginBottom: '1.5rem', fontSize: '1.1rem' }}>
          {'נראה שפתחת את הקישור ממכשיר אחר'}
        </Typography>

        <Typography sx={{ marginBottom: '1rem', fontSize: '0.95rem', color: '#666' }}>
          {'נא להזין את כתובת הדוא"ל שאליה נשלח הקישור'}
        </Typography>

        <TextField
          fullWidth
          type="email"
          label={t('email') || 'כתובת דוא"ל'}
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setError('');
          }}
          error={!!error}
          helperText={error}
          disabled={loading}
          className={classes.textField}
          autoFocus
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleVerify();
            }
          }}
        />
      </DialogContent>

      <DialogActions sx={{ padding: '1rem 2rem 2rem', justifyContent: 'center', gap: 2 }}>
        <Button
          onClick={onCancel}
          disabled={loading}
          className={classes.cancelButton}
        >
          {t('cancel') || 'ביטול'}
        </Button>

        <Button
          onClick={handleVerify}
          disabled={loading}
          className={classes.button}
          startIcon={loading && <CircularProgress size={20} sx={{ color: 'white' }} />}
        >
          {loading ? (t('verifying') || 'מאמת...') : (t('verify') || 'אימות')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailVerificationDialog;